import React, { useEffect, useEffectEvent, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import {
  Navigate,
  Route,
  Routes,
  useHref,
  useLocation,
  // oxlint-disable-next-line eslint/no-restricted-imports
  useNavigate as useNavigateRouter,
} from 'react-router';

import { useResponsive } from '@actual-app/components/hooks/useResponsive';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';
import * as undo from '@actual-app/core/platform/client/undo';

import { sync } from '#app/appSlice';
import { ProtectedRoute } from '#auth/ProtectedRoute';
import { Permissions } from '#auth/types';
import { useMetaThemeColor } from '#hooks/useMetaThemeColor';
import { useNavigate } from '#hooks/useNavigate';
import { ScrollProvider } from '#hooks/useScrollListener';
import { useDispatch } from '#redux';
import { faltaPermisoUso, marcarPantalla, registrarUso } from '#uso';

import { UserAccessPage } from './admin/UserAccess/UserAccessPage';
import { UserDirectoryPage } from './admin/UserDirectory/UserDirectoryPage';
import { BankSyncStatus } from './BankSyncStatus';
import { CommandBar } from './CommandBar';
import { ContextMenu } from './ContextMenu';
import { CofrePresupuesto } from './cuenta/CofrePresupuesto';
import { SubidaAutomatica } from './cuenta/SubidaAutomatica';
import { EnableBankingCallback } from './EnableBankingCallback';
import { FeatureErrorFallback } from './FeatureErrorFallback';
import { GlobalKeys } from './GlobalKeys';
import { useSincronizacionAutomatica } from './mobile/accounts/useSincronizacionAutomatica';
import {
  aplazadaEnSesion,
  aplazarEnSesion,
} from './mobile/actualizacion/actualizacion';
import { VentanaActualizacion } from './mobile/actualizacion/VentanaActualizacion';
import { AhorrosPage, RUTA_AHORROS } from './mobile/ahorros/AhorrosPage';
import { AnualPage } from './mobile/anual/AnualPage';
import { useAvisosSegundoPlano } from './mobile/avisos/useAvisosSegundoPlano';
import { useIrDesdeAviso } from './mobile/avisos/useIrDesdeAviso';
import { MobileBankSyncAccountEditPage } from './mobile/banksync/MobileBankSyncAccountEditPage';
import { BitacoraPage } from './mobile/bitacora/BitacoraPage';
import { AsignarMesPage } from './mobile/budget/AsignarMesPage';
import { EditarCategoriasPage } from './mobile/budget/EditarCategoriasPage';
import { FichaCategoriaPage } from './mobile/budget/ficha/FichaCategoriaPage';
import {
  ProveedorLlegadas,
  RUTA_BITACORA,
} from './mobile/budget/travesia/ProveedorLlegadas';
import { CompartidoPage } from './mobile/compartido/CompartidoPage';
import { useIrACompartido } from './mobile/compartido/useIrACompartido';
import { Encuesta } from './mobile/encuesta/Encuesta';
import { GastosPage } from './mobile/gastos/GastosPage';
import { GaviPage } from './mobile/gavi/GaviPage';
import { RUTA_GAVI } from './mobile/gavi/useGaviInteligente';
import { useCuadreHistoria } from './mobile/historia/useCuadreHistoria';
import { ImportarExtractoPage } from './mobile/importar/ImportarExtractoPage';
import { InicioPage } from './mobile/inicio/InicioPage';
import { MasPage } from './mobile/mas/MasPage';
import { MobileNavTabs } from './mobile/MobileNavTabs';
import { NominaPage } from './mobile/nomina/NominaPage';
import { PlanPagaPage } from './mobile/nomina/PlanPagaPage';
import { Novedades } from './mobile/novedades/Novedades';
import { ProveedorOpciones } from './mobile/opciones/OpcionesPantalla';
import { AvisoPruebaRumbo } from './mobile/plan/AvisoPruebaRumbo';
import { PuertaPlan } from './mobile/plan/PuertaPlan';
import {
  AvisoDatosInventados,
  avisoDatosPendiente,
} from './mobile/prueba/AvisoDatosInventados';
import { PuentePage, RUTA_PUENTE } from './mobile/puente/PuentePage';
import { RevisarPage } from './mobile/revisar/RevisarPage';
import { TransactionEdit } from './mobile/transactions/TransactionEdit';
import { PrimeraTravesia } from './mobile/travesia/PrimeraTravesia';
import {
  marcarBase,
  ponerInicioDebajo,
  RUTA_INICIO,
} from './mobile/ui/pestanas';
import { PermisoUso } from './mobile/uso/PermisoUso';
import { Notifications } from './Notifications';
import { MobilePageHeaderProvider, MobilePageHeaderSlot } from './Page';
import { Reports } from './reports';
import { NarrowAlternate } from './responsive';
import { useMultiuserEnabled } from './ServerContext';
import { Settings } from './settings';
import { FloatableSidebar } from './sidebar';
import { ManageTagsPage } from './tags/ManageTagsPage';
import { Titlebar } from './Titlebar';

function WideNotSupported({
  children,
  redirectTo = '/budget',
}: {
  redirectTo?: string;
  children: ReactElement;
}) {
  const { isNarrowWidth } = useResponsive();
  const navigate = useNavigate();
  useEffect(() => {
    if (!isNarrowWidth) {
      void navigate(redirectTo);
    }
  }, [isNarrowWidth, navigate, redirectTo]);
  return isNarrowWidth ? children : null;
}

/**
 * Presupuesto: la raíz lleva a la pantalla de inicio en el móvil y al
 * presupuesto en escritorio (que sigue como en Actual).
 */
function RaizRedirect() {
  const { isNarrowWidth } = useResponsive();
  return <Navigate to={isNarrowWidth ? '/inicio' : '/budget'} replace />;
}

function RouterBehaviors() {
  const location = useLocation();
  const href = useHref(location);
  const { isNarrowWidth } = useResponsive();
  const navegar = useNavigateRouter();
  // Móvil: Inicio es la base del «atrás». Si la app se abre en otra
  // pantalla, Inicio queda debajo (ver `ui/pestanas`).
  const basePuesta = useRef(false);
  useEffect(() => {
    if (!isNarrowWidth || basePuesta.current || location.pathname === '/') {
      return;
    }
    basePuesta.current = true;
    if (location.pathname === RUTA_INICIO) {
      marcarBase();
      return;
    }
    ponerInicioDebajo(
      location.pathname + location.search + location.hash,
      navegar,
    );
  }, [isNarrowWidth, location, navegar]);
  useEffect(() => {
    undo.setUndoState('url', href);
  }, [href]);
  // Rumbo: datos de uso (pantalla y tiempo en la anterior), ver src/uso.
  useEffect(() => {
    marcarPantalla(location.pathname);
  }, [location.pathname]);

  return null;
}

export function FinancesApp() {
  const { isNarrowWidth } = useResponsive();
  useMetaThemeColor(theme.mobileViewTheme);

  const location = useLocation();
  const dispatch = useDispatch();

  const multiuserEnabled = useMultiuserEnabled();

  // Beta: el aviso de datos inventados va primero; luego el permiso de uso
  // y después las novedades de la versión.
  const [avisoDatosAbierto, setAvisoDatosAbierto] =
    useState(avisoDatosPendiente);
  // El permiso de uso sale antes que las novedades de la versión.
  const [permisoAbierto, setPermisoAbierto] = useState(faltaPermisoUso);
  const [novedadesVistas, setNovedadesVistas] = useState(false);
  const [ventanaActualizacion, setVentanaActualizacion] = useState<
    'oculta' | 'visible' | 'aplicando'
  >('oculta');
  useSincronizacionAutomatica();
  // Notificaciones con la app cerrada.
  useAvisosSegundoPlano();
  // Al pulsar un aviso, abrir la pantalla que anuncia.
  useIrDesdeAviso();
  // Historial desde el principio: los meses de antes de Rumbo, cuadrados.
  useCuadreHistoria();
  // Extracto en dos toques compartido con la app cerrada.
  useIrACompartido();

  const init = useEffectEvent(() => {
    // Wait a little bit to make sure the sync button will get the
    // sync start event. This can be improved later.
    setTimeout(async () => {
      await dispatch(sync());
    }, 100);

    async function run() {
      await global.Actual.waitForUpdateReadyForDownload(); // This will only resolve when an update is ready
      // Rumbo: ventana centrada en vez de la notificación pegajosa. Si ya
      // pulsó «Más tarde» en esta sesión, no se vuelve a mostrar (puede
      // actualizar desde Ajustes).
      if (aplazadaEnSesion()) {
        return;
      }
      setVentanaActualizacion('visible');
      registrarUso('actualizacion_mostrada', { origen: 'inicio' });
    }

    void run();
  });

  useEffect(() => init(), []);

  const scrollableRef = useRef<HTMLDivElement>(null);

  return (
    <View style={{ height: '100%' }}>
      <RouterBehaviors />
      <GlobalKeys />
      <CommandBar />
      <ContextMenu />
      {ventanaActualizacion !== 'oculta' && (
        <VentanaActualizacion
          aplicando={ventanaActualizacion === 'aplicando'}
          onActualizar={() => {
            setVentanaActualizacion('aplicando');
            registrarUso('actualizacion_aplicada', { origen: 'inicio' });
            void global.Actual.applyAppUpdate();
          }}
          onMasTarde={() => {
            aplazarEnSesion();
            setVentanaActualizacion('oculta');
            registrarUso('actualizacion_mas_tarde', { origen: 'inicio' });
          }}
        />
      )}
      <View
        style={{
          flexDirection: 'row',
          backgroundColor: theme.pageBackground,
          flex: 1,
        }}
      >
        <FloatableSidebar />

        <View
          style={{
            color: theme.pageText,
            backgroundColor: theme.pageBackground,
            flex: 1,
            overflow: 'hidden',
            width: '100%',
          }}
        >
          <ScrollProvider
            isDisabled={!isNarrowWidth}
            scrollableRef={scrollableRef}
          >
            <MobilePageHeaderProvider>
              <ProveedorOpciones>
                {/* Primera travesía (pasos 2–8): su contexto llega a las
                      rutas y a la barra de pestañas. */}
                <PrimeraTravesia>
                  {/* Travesías: detector de llegadas (una sola vez) y la
                      llegada de la botella a pantalla completa. */}
                  <ProveedorLlegadas>
                    <View
                      ref={scrollableRef}
                      data-travesia-desplaza
                      style={{
                        flex: 1,
                        overflow: 'auto',
                        position: 'relative',
                      }}
                    >
                      <Titlebar
                        style={{
                          WebkitAppRegion: 'drag',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          zIndex: 1000,
                        }}
                      />
                      <Notifications />
                      <CofrePresupuesto />
                      <SubidaAutomatica />
                      <BankSyncStatus />
                      {isNarrowWidth && <MobilePageHeaderSlot />}

                      <Routes>
                        <Route path="/" element={<RaizRedirect />} />

                        <Route
                          path="/inicio"
                          element={
                            <ErrorBoundary
                              FallbackComponent={FeatureErrorFallback}
                              resetKeys={[location.pathname]}
                            >
                              <WideNotSupported>
                                <InicioPage />
                              </WideNotSupported>
                            </ErrorBoundary>
                          }
                        />

                        <Route
                          path="/asignar"
                          element={
                            <ErrorBoundary
                              FallbackComponent={FeatureErrorFallback}
                              resetKeys={[location.pathname]}
                            >
                              <WideNotSupported>
                                <AsignarMesPage />
                              </WideNotSupported>
                            </ErrorBoundary>
                          }
                        />

                        <Route
                          path="/categorias/editar"
                          element={
                            <ErrorBoundary
                              FallbackComponent={FeatureErrorFallback}
                              resetKeys={[location.pathname]}
                            >
                              <WideNotSupported>
                                <EditarCategoriasPage />
                              </WideNotSupported>
                            </ErrorBoundary>
                          }
                        />

                        <Route
                          path="/revisar"
                          element={
                            <ErrorBoundary
                              FallbackComponent={FeatureErrorFallback}
                              resetKeys={[location.pathname]}
                            >
                              <WideNotSupported>
                                <RevisarPage />
                              </WideNotSupported>
                            </ErrorBoundary>
                          }
                        />

                        <Route
                          path="/nomina"
                          element={
                            <ErrorBoundary
                              FallbackComponent={FeatureErrorFallback}
                              resetKeys={[location.pathname]}
                            >
                              <WideNotSupported>
                                <NominaPage />
                              </WideNotSupported>
                            </ErrorBoundary>
                          }
                        />

                        <Route
                          path="/pagas/:clave"
                          element={
                            <ErrorBoundary
                              FallbackComponent={FeatureErrorFallback}
                              resetKeys={[location.pathname]}
                            >
                              <WideNotSupported>
                                <PlanPagaPage />
                              </WideNotSupported>
                            </ErrorBoundary>
                          }
                        />

                        <Route
                          path="/anual"
                          element={
                            <ErrorBoundary
                              FallbackComponent={FeatureErrorFallback}
                              resetKeys={[location.pathname]}
                            >
                              <WideNotSupported>
                                <AnualPage />
                              </WideNotSupported>
                            </ErrorBoundary>
                          }
                        />

                        <Route
                          path="/categories/:id/ficha"
                          element={
                            <ErrorBoundary
                              FallbackComponent={FeatureErrorFallback}
                              resetKeys={[location.pathname]}
                            >
                              <WideNotSupported>
                                <FichaCategoriaPage />
                              </WideNotSupported>
                            </ErrorBoundary>
                          }
                        />

                        <Route
                          path={RUTA_AHORROS}
                          element={
                            <ErrorBoundary
                              FallbackComponent={FeatureErrorFallback}
                              resetKeys={[location.pathname]}
                            >
                              <WideNotSupported>
                                <AhorrosPage />
                              </WideNotSupported>
                            </ErrorBoundary>
                          }
                        />

                        <Route
                          path={RUTA_BITACORA}
                          element={
                            <ErrorBoundary
                              FallbackComponent={FeatureErrorFallback}
                              resetKeys={[location.pathname]}
                            >
                              <WideNotSupported>
                                <BitacoraPage />
                              </WideNotSupported>
                            </ErrorBoundary>
                          }
                        />

                        <Route path="/reports/*" element={<Reports />} />

                        <Route
                          path="/gastos"
                          element={
                            <ErrorBoundary
                              FallbackComponent={FeatureErrorFallback}
                              resetKeys={[location.pathname]}
                            >
                              <WideNotSupported redirectTo="/accounts">
                                <GastosPage />
                              </WideNotSupported>
                            </ErrorBoundary>
                          }
                        />

                        <Route
                          path="/mas"
                          element={
                            <ErrorBoundary
                              FallbackComponent={FeatureErrorFallback}
                              resetKeys={[location.pathname]}
                            >
                              <WideNotSupported>
                                <MasPage />
                              </WideNotSupported>
                            </ErrorBoundary>
                          }
                        />

                        <Route
                          path="/budget"
                          element={
                            <ErrorBoundary
                              FallbackComponent={FeatureErrorFallback}
                              resetKeys={[location.pathname]}
                            >
                              <NarrowAlternate name="Budget" />
                            </ErrorBoundary>
                          }
                        />

                        <Route
                          path="/schedules"
                          element={
                            <ErrorBoundary
                              FallbackComponent={FeatureErrorFallback}
                              resetKeys={[location.pathname]}
                            >
                              <NarrowAlternate name="Schedules" />
                            </ErrorBoundary>
                          }
                        />
                        <Route
                          path="/schedules/:id"
                          element={
                            <ErrorBoundary
                              FallbackComponent={FeatureErrorFallback}
                              resetKeys={[location.pathname]}
                            >
                              <WideNotSupported>
                                <NarrowAlternate name="ScheduleEdit" />
                              </WideNotSupported>
                            </ErrorBoundary>
                          }
                        />

                        <Route
                          path="/payees"
                          element={
                            <ErrorBoundary
                              FallbackComponent={FeatureErrorFallback}
                              resetKeys={[location.pathname]}
                            >
                              <NarrowAlternate name="Payees" />
                            </ErrorBoundary>
                          }
                        />
                        <Route
                          path="/payees/:id"
                          element={
                            <ErrorBoundary
                              FallbackComponent={FeatureErrorFallback}
                              resetKeys={[location.pathname]}
                            >
                              <WideNotSupported>
                                <NarrowAlternate name="PayeeEdit" />
                              </WideNotSupported>
                            </ErrorBoundary>
                          }
                        />
                        <Route
                          path="/rules"
                          element={
                            <ErrorBoundary
                              FallbackComponent={FeatureErrorFallback}
                              resetKeys={[location.pathname]}
                            >
                              <NarrowAlternate name="Rules" />
                            </ErrorBoundary>
                          }
                        />
                        <Route
                          path="/rules/:id"
                          element={
                            <ErrorBoundary
                              FallbackComponent={FeatureErrorFallback}
                              resetKeys={[location.pathname]}
                            >
                              <NarrowAlternate name="RuleEdit" />
                            </ErrorBoundary>
                          }
                        />
                        <Route
                          path="/bank-sync"
                          element={
                            <ErrorBoundary
                              FallbackComponent={FeatureErrorFallback}
                              resetKeys={[location.pathname]}
                            >
                              <NarrowAlternate name="BankSync" />
                            </ErrorBoundary>
                          }
                        />
                        <Route
                          path="/bank-sync/account/:accountId/edit"
                          element={
                            <ErrorBoundary
                              FallbackComponent={FeatureErrorFallback}
                              resetKeys={[location.pathname]}
                            >
                              <WideNotSupported redirectTo="/bank-sync">
                                <MobileBankSyncAccountEditPage />
                              </WideNotSupported>
                            </ErrorBoundary>
                          }
                        />
                        <Route path="/tags" element={<ManageTagsPage />} />
                        <Route path="/settings" element={<Settings />} />
                        <Route
                          path={RUTA_PUENTE}
                          element={
                            <ErrorBoundary
                              FallbackComponent={FeatureErrorFallback}
                              resetKeys={[location.pathname]}
                            >
                              <PuentePage />
                            </ErrorBoundary>
                          }
                        />
                        <Route
                          path={RUTA_GAVI}
                          element={
                            <ErrorBoundary
                              FallbackComponent={FeatureErrorFallback}
                              resetKeys={[location.pathname]}
                            >
                              <WideNotSupported>
                                <GaviPage />
                              </WideNotSupported>
                            </ErrorBoundary>
                          }
                        />

                        <Route
                          path="/enablebanking/auth_callback"
                          element={<EnableBankingCallback />}
                        />

                        <Route
                          path="/accounts"
                          element={
                            <ErrorBoundary
                              FallbackComponent={FeatureErrorFallback}
                              resetKeys={[location.pathname]}
                            >
                              <NarrowAlternate name="Accounts" />
                            </ErrorBoundary>
                          }
                        />

                        <Route
                          path="/compartido"
                          element={
                            <ErrorBoundary
                              FallbackComponent={FeatureErrorFallback}
                              resetKeys={[location.pathname]}
                            >
                              <WideNotSupported>
                                <CompartidoPage />
                              </WideNotSupported>
                            </ErrorBoundary>
                          }
                        />

                        <Route
                          path="/accounts/:id/importar"
                          element={
                            <ErrorBoundary
                              FallbackComponent={FeatureErrorFallback}
                              resetKeys={[location.pathname]}
                            >
                              <WideNotSupported>
                                <ImportarExtractoPage />
                              </WideNotSupported>
                            </ErrorBoundary>
                          }
                        />

                        <Route
                          path="/accounts/:id"
                          element={
                            <ErrorBoundary
                              FallbackComponent={FeatureErrorFallback}
                              resetKeys={[location.pathname]}
                            >
                              <NarrowAlternate name="Account" />
                            </ErrorBoundary>
                          }
                        />

                        <Route
                          path="/transactions/:transactionId"
                          element={
                            <ErrorBoundary
                              FallbackComponent={FeatureErrorFallback}
                              resetKeys={[location.pathname]}
                            >
                              <WideNotSupported>
                                <TransactionEdit />
                              </WideNotSupported>
                            </ErrorBoundary>
                          }
                        />

                        <Route
                          path="/categories/:id"
                          element={
                            <ErrorBoundary
                              FallbackComponent={FeatureErrorFallback}
                              resetKeys={[location.pathname]}
                            >
                              <NarrowAlternate name="Category" />
                            </ErrorBoundary>
                          }
                        />
                        {multiuserEnabled && (
                          <Route
                            path="/user-directory"
                            element={
                              <ProtectedRoute
                                permission={Permissions.ADMINISTRATOR}
                                element={<UserDirectoryPage />}
                              />
                            }
                          />
                        )}
                        {multiuserEnabled && (
                          <Route
                            path="/user-access"
                            element={
                              <ProtectedRoute
                                permission={Permissions.ADMINISTRATOR}
                                validateOwner
                                element={<UserAccessPage />}
                              />
                            }
                          />
                        )}
                        {/* redirect all other traffic to the budget page */}
                        <Route
                          path="/*"
                          element={<Navigate to="/budget" replace />}
                        />
                      </Routes>
                    </View>

                    {/* La barra decide en qué rutas se pinta (`barraVisibleEn`). */}
                    <MobileNavTabs />
                    {/* Puerta de planes: «solo ver» al terminar el periodo. */}
                    <PuertaPlan />
                    {/* Beta: «Usa datos inventados» (una vez, no al admin). */}
                    <AvisoDatosInventados
                      onCerrado={() => setAvisoDatosAbierto(false)}
                    />
                    {/* Pide permiso para los datos de uso (una vez). */}
                    <PermisoUso
                      esperar={avisoDatosAbierto}
                      onCerrado={() => setPermisoAbierto(false)}
                    />
                    {/* Gavi cuenta las novedades tras actualizar (una vez por versión). */}
                    <Novedades
                      esperar={permisoAbierto || avisoDatosAbierto}
                      onCerrado={() => setNovedadesVistas(true)}
                    />
                    {/* Encuestas de Gavi (día 10 de uso y día 40 de la prueba). */}
                    <Encuesta esperar={permisoAbierto || avisoDatosAbierto} />
                    {/* Gavi y la categoría Rumbo durante la prueba (en Inicio). */}
                    <AvisoPruebaRumbo
                      esperar={permisoAbierto || avisoDatosAbierto}
                      novedadesVistas={novedadesVistas}
                    />
                  </ProveedorLlegadas>
                </PrimeraTravesia>
              </ProveedorOpciones>
            </MobilePageHeaderProvider>
          </ScrollProvider>
        </View>
      </View>
    </View>
  );
}
